import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from '../animations/useReducedMotion';
import './dot-matrix-loader.css';

const REVOLUTION_MS = 1400;
const MIN_OPACITY = 0.16;
const TRAIL_LENGTH = 0.28;

function createClockwiseRing(gridSize: number, inset: number): readonly (readonly [number, number])[] {
    const start = inset;
    const end = gridSize - inset - 1;
    const ring: [number, number][] = [];

    for (let col = start; col <= end; col++) ring.push([start, col]);
    for (let row = start + 1; row <= end; row++) ring.push([row, end]);
    for (let col = end - 1; col >= start; col--) ring.push([end, col]);
    for (let row = end - 1; row > start; row--) ring.push([row, start]);

    return ring;
}

const RINGS = {
    4: {
        outer: createClockwiseRing(4, 0),
        inner: createClockwiseRing(4, 1),
    },
    5: {
        outer: createClockwiseRing(5, 0),
        inner: createClockwiseRing(5, 1),
    },
} as const;

export type DotMatrixLoaderVariant = 'default' | 'compact';

/**
 * Dual-ring counter-rotating 5x5 / 4x4 dot-matrix loader adapted from
 * thesysdev/openui (`packages/react-ui/src/components/DotMatrixLoader`, MIT).
 * Respects `prefers-reduced-motion` and pauses when the document is hidden.
 */
export const DotMatrixLoader: React.FC<{
    variant?: DotMatrixLoaderVariant;
    size?: number;
    label?: string;
    className?: string;
}> = ({ variant = 'default', size, label, className = '' }) => {
    const reduced = useReducedMotion();
    const dotsRef = useRef<Map<string, HTMLSpanElement>>(new Map());
    const isCompact = variant === 'compact';
    const gridSize = isCompact ? 4 : 5;
    const rings = RINGS[gridSize];
    const resolvedSize = size ?? (isCompact ? 16 : 28);

    useEffect(() => {
        const dots = dotsRef.current;
        dots.forEach((dot, key) => {
            const [r, c] = key.split('-').map(Number);
            const isCorner = (r === 0 || r === gridSize - 1) && (c === 0 || c === gridSize - 1);
            dot.style.opacity = reduced ? (isCorner ? '0.85' : '0.32') : String(MIN_OPACITY);
        });
        if (reduced) return;

        const paintRing = (ring: readonly (readonly [number, number])[], head: number) => {
            const tail = ring.length * TRAIL_LENGTH;
            for (let i = 0; i < ring.length; i++) {
                const cell = ring[i];
                const dot = cell && dots.get(`${cell[0]}-${cell[1]}`);
                if (!dot) continue;
                const distance = (head - i + ring.length) % ring.length;
                const trailIntensity = Math.exp(-distance / tail);
                const forwardDistance = (i - head + ring.length) % ring.length;
                const leadProgress = forwardDistance < 1 ? 1 - forwardDistance : 0;
                const leadIntensity = leadProgress * leadProgress * (3 - 2 * leadProgress);
                const intensity = Math.max(trailIntensity, leadIntensity);
                dot.style.opacity = (MIN_OPACITY + (1 - MIN_OPACITY) * intensity).toFixed(3);
            }
        };

        let frame = 0;
        const tick = (now: number) => {
            if (!document.hidden) {
                const progress = (now % REVOLUTION_MS) / REVOLUTION_MS;
                paintRing(rings.outer, progress * rings.outer.length);
                paintRing(rings.inner, (1 - progress) * rings.inner.length);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [rings, gridSize, reduced]);

    const cells: React.ReactNode[] = [];
    for (let row = 0; row < gridSize; row++) {
        for (let col = 0; col < gridSize; col++) {
            const key = `${row}-${col}`;
            cells.push(
                <span
                    key={key}
                    data-testid="dot-matrix-cell"
                    className="bs-dot-matrix__dot"
                    ref={el => {
                        if (el) dotsRef.current.set(key, el);
                        else dotsRef.current.delete(key);
                    }}
                />,
            );
        }
    }

    return (
        <span
            className={`bs-dot-matrix ${className}`.trim()}
            role={label ? 'status' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            style={{
                '--dot-matrix-grid': gridSize,
                '--dot-matrix-size': `${resolvedSize}px`,
            } as React.CSSProperties}
        >
            {cells}
        </span>
    );
};

export default DotMatrixLoader;
